import { UserRound, VideoOff } from "lucide-react";
import type { RefObject } from "react";

import { cn } from "@/lib/utils";
import type { CallState } from "@/types/call";

interface CallStageProps {
  state: CallState;
  patientName: string;
  hasRemoteStream: boolean;
  remoteUserPresent: boolean;
  cameraOff: boolean;
  /** Callback ref: the engine mounts the self-view as soon as this node exists. */
  setLocalView: (node: HTMLDivElement | null) => void;
  remoteVideoRef: RefObject<HTMLVideoElement | null>;
}

function remoteMessage(props: CallStageProps): { title: string; hint?: string } | null {
  const { state, hasRemoteStream, remoteUserPresent, patientName } = props;
  if (hasRemoteStream) return null;
  if (state === "patientLeft")
    return { title: "Patient disconnected", hint: "Waiting for the patient to reconnect…" };
  if (state === "reconnecting")
    return { title: "Reconnecting…", hint: "Trying to restore the connection." };
  if (remoteUserPresent) return { title: "Connecting…", hint: `${patientName} is joining.` };
  return { title: "Waiting for patient to join…", hint: `${patientName} hasn't joined yet.` };
}

/** Patient video as the main stage, doctor self-view as a compact overlay. */
export function CallStage(props: CallStageProps) {
  const { setLocalView, remoteVideoRef, cameraOff } = props;
  const message = remoteMessage(props);

  return (
    <div
      className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-border bg-secondary sm:aspect-video"
      role="group"
      aria-label="Video consultation"
    >
      <video
        ref={remoteVideoRef}
        aria-label="Patient video"
        playsInline
        autoPlay
        className={cn("size-full object-cover", message && "invisible")}
      />

      {message && (
        <div
          className="absolute inset-0 grid place-items-center px-6 text-center"
          role="status"
          aria-live="polite"
        >
          <div>
            <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-muted text-muted-foreground">
              <UserRound className="size-6" />
            </span>
            <p className="mt-4 font-display text-lg font-semibold">{message.title}</p>
            {message.hint && <p className="mt-1 text-sm text-muted-foreground">{message.hint}</p>}
          </div>
        </div>
      )}

      <div className="absolute right-3 bottom-3 w-28 overflow-hidden rounded-xl border border-border bg-muted shadow-lg sm:w-44">
        <div className="relative aspect-video">
          {/* Container, not a <video>: the SDK mounts its own wrapper div with a
              <video> inside it, so the sizing rules use a descendant selector —
              a direct-child one never matches, and the SDK's inline
              object-fit:contain would letterbox a 4:3 camera in this 16:9 box. */}
          <div
            ref={setLocalView}
            aria-label="Your camera preview"
            className={cn(
              "size-full [&_video]:size-full [&_video]:!object-cover [&>div]:size-full",
              cameraOff && "invisible",
            )}
          />
          {cameraOff && (
            <div className="absolute inset-0 grid place-items-center bg-muted text-muted-foreground">
              <VideoOff className="size-4" aria-hidden />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
